import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  BarChart3, RefreshCw, AlertCircle, TrendingUp, Activity, HelpCircle, 
  Calendar, DollarSign, Cpu, Mic, FileText, Eye, EyeOff, Save, ChevronDown
} from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format date as YYYY-MM-DD
const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

// Get date range presets
const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  
  let start = new Date();
  
  switch (preset) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case '7days':
      start.setDate(today.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case '30days':
      start.setDate(today.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'thisMonth':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'lastMonth':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start, end: endOfLastMonth };
    default:
      start.setDate(today.getDate() - 6);
      start.setHours(0, 0, 0, 0);
  }
  
  return { start, end: today };
};

export default function OpenRouterStats() {
  // Date range state
  const [datePreset, setDatePreset] = useState('7days');
  const [startDate, setStartDate] = useState(() => formatDateForAPI(getDateRange('7days').start));
  const [endDate, setEndDate] = useState(() => formatDateForAPI(getDateRange('7days').end));
  
  // OpenRouter state
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // ElevenLabs state
  const [loadingElevenLabs, setLoadingElevenLabs] = useState(false);
  const [elevenLabsStats, setElevenLabsStats] = useState(null);
  const [elevenLabsError, setElevenLabsError] = useState(null);
  
  // OpenAI state
  const [loadingOpenAI, setLoadingOpenAI] = useState(false);
  const [openAIStats, setOpenAIStats] = useState(null);
  const [openAIError, setOpenAIError] = useState(null);
  const [openAIProjects, setOpenAIProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  
  // OpenAI Admin Key state
  const [adminKey, setAdminKey] = useState('');
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [savingAdminKey, setSavingAdminKey] = useState(false);
  const [adminKeyConfigured, setAdminKeyConfigured] = useState(false);
  
  // Internal Usage Stats state (by functionality)
  const [internalStats, setInternalStats] = useState(null);
  const [loadingInternal, setLoadingInternal] = useState(false);
  const [actors, setActors] = useState([]);
  const [selectedActorFilter, setSelectedActorFilter] = useState('all');

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      setStartDate(formatDateForAPI(range.start));
      setEndDate(formatDateForAPI(range.end));
    }
  };

  // Fetch API keys to check if admin key is configured
  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.openai_admin_key) {
        setAdminKeyConfigured(true);
        setAdminKey(response.data.openai_admin_key);
      }
    } catch (error) {
      console.error("Error fetching API keys:", error);
    }
  };

  // Save OpenAI Admin Key
  const saveAdminKey = async () => {
    if (!adminKey.trim()) {
      toast.error("Please enter an OpenAI Admin API key");
      return;
    }
    
    setSavingAdminKey(true);
    try {
      const token = localStorage.getItem("token");
      
      // First get existing settings
      const existingResponse = await axios.get(`${API}/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update with new admin key
      await axios.put(`${API}/settings/api-keys`, {
        ...existingResponse.data,
        openai_admin_key: adminKey
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setAdminKeyConfigured(true);
      toast.success("OpenAI Admin API key saved successfully!");
      
      // Fetch projects and stats
      fetchOpenAIProjects();
      fetchOpenAIStats();
    } catch (error) {
      toast.error("Failed to save API key: " + (error.response?.data?.detail || error.message));
    } finally {
      setSavingAdminKey(false);
    }
  };

  // Fetch OpenRouter stats
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/settings/openrouter-stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch stats";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch ElevenLabs stats
  const fetchElevenLabsStats = async () => {
    setLoadingElevenLabs(true);
    setElevenLabsError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/elevenlabs-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setElevenLabsStats(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch ElevenLabs stats";
      setElevenLabsError(errorMsg);
    } finally {
      setLoadingElevenLabs(false);
    }
  };

  // Fetch OpenAI projects
  const fetchOpenAIProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/openai-projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenAIProjects(response.data.projects || []);
    } catch (error) {
      console.log("Could not fetch OpenAI projects:", error.response?.data?.detail);
    }
  };

  // Fetch OpenAI stats
  const fetchOpenAIStats = useCallback(async () => {
    setLoadingOpenAI(true);
    setOpenAIError(null);
    
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedProject && selectedProject !== 'all') {
        params.append('project_id', selectedProject);
      }
      
      const response = await axios.get(`${API}/settings/openai-stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setOpenAIStats(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch OpenAI stats";
      setOpenAIError(errorMsg);
    } finally {
      setLoadingOpenAI(false);
    }
  }, [startDate, endDate, selectedProject]);

  // Fetch Internal Usage Stats (by functionality)
  const fetchInternalStats = useCallback(async () => {
    setLoadingInternal(true);
    
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedActorFilter && selectedActorFilter !== 'all') {
        params.append('actor_id', selectedActorFilter);
      }
      
      const response = await axios.get(`${API}/settings/internal-usage-stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setInternalStats(response.data);
    } catch (error) {
      console.error("Error fetching internal usage stats:", error);
    } finally {
      setLoadingInternal(false);
    }
  }, [startDate, endDate, selectedActorFilter]);

  // Fetch AI Actors for filtering
  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActors(response.data || []);
    } catch (error) {
      console.error("Error fetching actors:", error);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchStats();
    fetchElevenLabsStats();
    fetchInternalStats();
    fetchActors();
  }, []);

  // Fetch OpenAI data when admin key is configured
  useEffect(() => {
    if (adminKeyConfigured) {
      fetchOpenAIProjects();
      fetchOpenAIStats();
    }
  }, [adminKeyConfigured]);

  // Refetch when date range changes
  useEffect(() => {
    if (datePreset !== 'custom') {
      fetchStats();
      fetchInternalStats();
      if (adminKeyConfigured) {
        fetchOpenAIStats();
      }
    }
  }, [startDate, endDate, fetchStats, fetchOpenAIStats, fetchInternalStats, adminKeyConfigured, datePreset]);

  // Refetch OpenAI stats when project changes
  useEffect(() => {
    if (adminKeyConfigured) {
      fetchOpenAIStats();
    }
  }, [selectedProject, adminKeyConfigured, fetchOpenAIStats]);

  // Refetch internal stats when actor filter changes
  useEffect(() => {
    fetchInternalStats();
  }, [selectedActorFilter, fetchInternalStats]);

  const handleRefresh = () => {
    fetchStats();
    fetchElevenLabsStats();
    fetchInternalStats();
    if (adminKeyConfigured) {
      fetchOpenAIStats();
    }
    toast.success("Stats refreshed successfully");
  };

  const handleApplyDateRange = () => {
    fetchStats();
    fetchInternalStats();
    if (adminKeyConfigured) {
      fetchOpenAIStats();
    }
    toast.success("Date range applied");
  };

  const formatNumber = (num) => {
    if (typeof num === 'string') return num;
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(4)}`;
  };

  const getUsagePercentage = (used, limit) => {
    if (typeof limit === 'string') return 0;
    if (!limit || limit === 0) return 0;
    return Math.round((used / limit) * 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return "text-red-600 bg-red-50";
    if (percentage >= 70) return "text-orange-600 bg-orange-50";
    if (percentage >= 50) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  return (
    <div className="p-8" data-testid="openrouter-stats-page">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Usage Statistics</h1>
            <p className="text-slate-600">Real-time usage statistics for your AI services</p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={loading || loadingElevenLabs || loadingOpenAI}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="refresh-stats-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || loadingElevenLabs || loadingOpenAI) ? 'animate-spin' : ''}`} />
            {(loading || loadingElevenLabs || loadingOpenAI) ? "Refreshing..." : "Refresh All Stats"}
          </Button>
        </div>

        {/* Date Range Selector */}
        <Card className="border-slate-200 shadow-sm mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Date Range Filter
            </CardTitle>
            <CardDescription>
              Select a date range to filter OpenRouter and OpenAI usage statistics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-slate-600 mb-2 block">Quick Select</Label>
                <Select value={datePreset} onValueChange={handleDatePresetChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="thisMonth">This Month</SelectItem>
                    <SelectItem value="lastMonth">Last Month</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 min-w-[150px]">
                <Label className="text-sm text-slate-600 mb-2 block">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full"
                />
              </div>
              
              <div className="flex-1 min-w-[150px]">
                <Label className="text-sm text-slate-600 mb-2 block">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full"
                />
              </div>
              
              {datePreset === 'custom' && (
                <Button 
                  onClick={handleApplyDateRange}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Apply Range
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* ============ USAGE BY FUNCTIONALITY SECTION ============ */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan-600" />
            Usage by Functionality
          </h2>
          
          {/* Actor Filter */}
          {actors.length > 0 && (
            <div className="mb-6">
              <Label className="text-sm text-slate-600 mb-2 block">Filter by AI Actor</Label>
              <Select value={selectedActorFilter} onValueChange={setSelectedActorFilter}>
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue placeholder="All Actors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actors</SelectItem>
                  {actors.map((actor) => (
                    <SelectItem key={actor.id} value={actor.id}>
                      {actor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {loadingInternal && !internalStats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 text-cyan-600 animate-spin mx-auto mb-3" />
                <p className="text-slate-600">Loading usage data...</p>
              </div>
            </div>
          )}

          {internalStats && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Total API Calls</span>
                      <Activity className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(internalStats.summary?.total_requests || 0)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">tracked internally</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Input Tokens</span>
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(internalStats.summary?.total_tokens_in || 0)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">prompt tokens</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Output Tokens</span>
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(internalStats.summary?.total_tokens_out || 0)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">completion tokens</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Total Tokens</span>
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(internalStats.summary?.total_tokens || 0)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">combined</p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage by Functionality - OpenRouter */}
              {internalStats.by_functionality?.openrouter?.length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>OpenRouter Usage by Feature</CardTitle>
                    <CardDescription>
                      Which features are using the most API calls
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Feature</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Input Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Output Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {internalStats.by_functionality.openrouter.map((item, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <span className="font-medium text-slate-900">{item.label}</span>
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.requests)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.tokens_in)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.tokens_out)}
                              </td>
                              <td className="text-right py-3 px-4 font-medium text-slate-900">
                                {formatNumber(item.total_tokens)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Usage by Functionality - OpenAI */}
              {internalStats.by_functionality?.openai?.length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>OpenAI Usage by Feature</CardTitle>
                    <CardDescription>
                      OpenAI API usage breakdown (Embeddings, Whisper, etc.)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Feature</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {internalStats.by_functionality.openai.map((item, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <span className="font-medium text-slate-900">{item.label}</span>
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.requests)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.tokens_in)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Usage by AI Actor */}
              {internalStats.by_actor?.length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>Usage by AI Actor</CardTitle>
                    <CardDescription>
                      API usage breakdown by each AI actor
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">AI Actor</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Input Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Output Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {internalStats.by_actor.map((item, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <span className="font-medium text-slate-900">{item.actor_name}</span>
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.requests)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.tokens_in)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(item.tokens_out)}
                              </td>
                              <td className="text-right py-3 px-4 font-medium text-slate-900">
                                {formatNumber(item.total_tokens)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No Data Message */}
              {(!internalStats.by_functionality?.openrouter?.length && 
                !internalStats.by_functionality?.openai?.length &&
                !internalStats.by_actor?.length) && (
                <Card className="border-slate-200 bg-slate-50">
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-600 mb-2">No Usage Data Yet</h3>
                      <p className="text-sm text-slate-500">
                        Usage tracking has been enabled. Data will appear here as you use the AI features.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
        
        {/* ============ OPENAI SECTION ============ */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Cpu className="w-6 h-6 text-green-600" />
            OpenAI Statistics
          </h2>

          {/* Admin Key Configuration */}
          {!adminKeyConfigured && (
            <Card className="border-green-200 bg-green-50 mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-green-600" />
                  Configure OpenAI Admin API Key
                </CardTitle>
                <CardDescription>
                  To view OpenAI usage statistics, you need to provide an Admin API key with usage read permissions.
                  <a 
                    href="https://platform.openai.com/api-keys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-700 underline ml-1"
                  >
                    Get your Admin key here
                  </a>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input
                      type={showAdminKey ? "text" : "password"}
                      placeholder="sk-admin-..."
                      value={adminKey}
                      onChange={(e) => setAdminKey(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAdminKey(!showAdminKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showAdminKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button 
                    onClick={saveAdminKey}
                    disabled={savingAdminKey}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingAdminKey ? "Saving..." : "Save Key"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* OpenAI Error State */}
          {openAIError && adminKeyConfigured && (
            <Card className="border-red-200 bg-red-50 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-1">Unable to fetch OpenAI statistics</h3>
                    <p className="text-sm text-red-800">{openAIError}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* OpenAI Loading State */}
          {loadingOpenAI && adminKeyConfigured && !openAIStats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 text-green-600 animate-spin mx-auto mb-3" />
                <p className="text-slate-600">Fetching OpenAI statistics...</p>
              </div>
            </div>
          )}

          {/* OpenAI Stats Display */}
          {openAIStats && adminKeyConfigured && (
            <>
              {/* Project Selector */}
              {openAIProjects.length > 0 && (
                <div className="mb-6">
                  <Label className="text-sm text-slate-600 mb-2 block">Filter by Project</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {openAIProjects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Total Cost</span>
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatCurrency(openAIStats.summary.total_cost)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {openAIStats.date_range.start} to {openAIStats.date_range.end}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Total Requests</span>
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(openAIStats.summary.total_requests)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">API calls made</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Total Tokens</span>
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(openAIStats.summary.total_tokens)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">input + output</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Audio (Whisper)</span>
                      <Mic className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(openAIStats.audio_transcriptions.total_seconds)}s
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(openAIStats.audio_transcriptions.total_requests)} transcriptions
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Completions Usage by Model */}
              {Object.keys(openAIStats.completions.models).length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>Completions Usage (GPT Models)</CardTitle>
                    <CardDescription>
                      Token usage breakdown by model for chat completions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Model</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Input Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Output Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(openAIStats.completions.models)
                            .sort((a, b) => (b[1].input_tokens + b[1].output_tokens) - (a[1].input_tokens + a[1].output_tokens))
                            .map(([model, data]) => (
                              <tr key={model} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-3 px-4">
                                  <span className="font-medium text-slate-900">{model}</span>
                                </td>
                                <td className="text-right py-3 px-4 text-slate-600">
                                  {formatNumber(data.requests)}
                                </td>
                                <td className="text-right py-3 px-4 text-slate-600">
                                  {formatNumber(data.input_tokens)}
                                </td>
                                <td className="text-right py-3 px-4 text-slate-600">
                                  {formatNumber(data.output_tokens)}
                                </td>
                                <td className="text-right py-3 px-4 font-medium text-slate-900">
                                  {formatNumber(data.input_tokens + data.output_tokens)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50">
                            <td className="py-3 px-4 font-semibold text-slate-900">Total</td>
                            <td className="text-right py-3 px-4 font-semibold text-slate-900">
                              {formatNumber(openAIStats.completions.total_requests)}
                            </td>
                            <td className="text-right py-3 px-4 font-semibold text-slate-900">
                              {formatNumber(openAIStats.completions.input_tokens)}
                            </td>
                            <td className="text-right py-3 px-4 font-semibold text-slate-900">
                              {formatNumber(openAIStats.completions.output_tokens)}
                            </td>
                            <td className="text-right py-3 px-4 font-semibold text-slate-900">
                              {formatNumber(openAIStats.completions.input_tokens + openAIStats.completions.output_tokens)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Embeddings Usage */}
              {Object.keys(openAIStats.embeddings.models).length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>Embeddings Usage (Knowledge Base)</CardTitle>
                    <CardDescription>
                      Token usage for text embeddings used in semantic search
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Model</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Input Tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(openAIStats.embeddings.models).map(([model, data]) => (
                            <tr key={model} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <span className="font-medium text-slate-900">{model}</span>
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(data.requests)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(data.input_tokens)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cost Breakdown */}
              {Object.keys(openAIStats.costs.by_line_item).length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>Cost Breakdown</CardTitle>
                    <CardDescription>
                      Costs grouped by service type
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(openAIStats.costs.by_line_item)
                        .sort((a, b) => b[1] - a[1])
                        .map(([item, cost]) => (
                          <div key={item} className="flex justify-between items-center py-2 border-b border-slate-100">
                            <span className="text-slate-700">{item}</span>
                            <span className="font-semibold text-slate-900">{formatCurrency(cost)}</span>
                          </div>
                        ))}
                      <div className="flex justify-between items-center py-2 pt-4 border-t-2 border-slate-200">
                        <span className="font-bold text-slate-900">Total</span>
                        <span className="font-bold text-green-600 text-lg">{formatCurrency(openAIStats.costs.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Cost Chart - Simple Table */}
              {openAIStats.costs.by_day.length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle>Daily Costs</CardTitle>
                    <CardDescription>
                      Cost breakdown by day
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-64">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-4 font-semibold text-slate-700">Date</th>
                            <th className="text-right py-2 px-4 font-semibold text-slate-700">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openAIStats.costs.by_day.map((day) => (
                            <tr key={day.date} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-4 text-slate-600">{day.date}</td>
                              <td className="text-right py-2 px-4 font-medium text-slate-900">
                                {formatCurrency(day.cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* ============ OPENROUTER SECTION ============ */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
            OpenRouter Statistics
          </h2>

          {/* Error State */}
          {error && (
            <Card className="border-red-200 bg-red-50 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-1">Unable to fetch statistics</h3>
                    <p className="text-sm text-red-800 mb-3">{error}</p>
                    {error.includes("not configured") && (
                      <Link to="/settings/api-keys">
                        <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100">
                          Configure API Key
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {loading && !stats && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-slate-600">Fetching your OpenRouter statistics...</p>
              </div>
            </div>
          )}

          {/* Stats Display */}
          {stats && !loading && (
            <>
              {/* Important Notice */}
              <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 text-2xl">⚠️</div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">Important: How Free Model Limits Work</h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      All free model requests (DeepSeek, Qwen, Llama, etc.) count toward your <strong>account-wide daily limit</strong> of {stats.key_info.daily_limit} requests/day. 
                      You <strong>cannot</strong> use 200 requests per model. For example, if you use 100 DeepSeek + 100 Qwen requests, you&apos;ve used 200 total from your account limit.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Account Type</span>
                      <Activity className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className={`text-2xl font-bold ${stats.key_info.is_free_tier ? 'text-blue-600' : 'text-green-600'}`}>
                      {stats.key_info.is_free_tier ? 'Free Tier' : 'Paid Account'}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Daily limit: {formatNumber(stats.key_info.daily_limit)} requests
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Today&apos;s Usage</span>
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(stats.key_info.usage_today)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(stats.key_info.remaining)} remaining today
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Weekly Usage</span>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(stats.key_info.usage_weekly)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">requests this week</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Monthly Usage</span>
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(stats.key_info.usage_monthly)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">requests this month</p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage Progress Bar */}
              {typeof stats.key_info.daily_limit === 'number' && (
                <Card className="border-slate-200 shadow-sm mb-8">
                  <CardHeader>
                    <CardTitle>Today&apos;s Usage Progress</CardTitle>
                    <CardDescription>
                      Your current usage against the daily limit ({formatNumber(stats.key_info.daily_limit)} requests)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700">
                          {formatNumber(stats.key_info.usage_today)} / {formatNumber(stats.key_info.daily_limit)} requests
                        </span>
                        <span className={`font-bold ${
                          getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit) >= 90 ? 'text-red-600' :
                          getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit) >= 70 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit)}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-4">
                        <div
                          className={`h-4 rounded-full transition-all ${
                            getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit) >= 90 ? 'bg-red-500' :
                            getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit) >= 70 ? 'bg-orange-500' :
                            'bg-indigo-500'
                          }`}
                          style={{ width: `${Math.min(getUsagePercentage(stats.key_info.usage_today, stats.key_info.daily_limit), 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Per-Model Breakdown */}
              {stats.models && stats.models.length > 0 && (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle>Per-Model Usage Breakdown</CardTitle>
                    <CardDescription>
                      Usage statistics for each model used in the selected date range
                      {stats.date_range && (
                        <span className="ml-2 text-indigo-600">
                          ({stats.date_range.start} to {stats.date_range.end})
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Model</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests (Total)</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests (Today)</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Tokens</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.models.map((model, index) => (
                            <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <div>
                                  <span className="font-medium text-slate-900">{model.name || model.model_id}</span>
                                  {model.model_id?.includes(':free') && (
                                    <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">FREE</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(model.requests_used || model.requests_total || 0)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(model.requests_today || 0)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {formatNumber(model.total_tokens || 0)}
                              </td>
                              <td className="text-right py-3 px-4 font-medium text-slate-900">
                                ${(model.cost_total || 0).toFixed(4)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* ============ ELEVENLABS SECTION ============ */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Mic className="w-6 h-6 text-purple-600" />
            ElevenLabs Statistics
          </h2>

          {/* ElevenLabs Error State */}
          {elevenLabsError && (
            <Card className="border-red-200 bg-red-50 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-1">Unable to fetch ElevenLabs statistics</h3>
                    <p className="text-sm text-red-800 mb-3">{elevenLabsError}</p>
                    {elevenLabsError.includes("not configured") && (
                      <Link to="/settings/api-keys">
                        <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100">
                          Configure API Key
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ElevenLabs Loading State */}
          {loadingElevenLabs && !elevenLabsStats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 text-purple-600 animate-spin mx-auto mb-3" />
                <p className="text-slate-600">Fetching ElevenLabs statistics...</p>
              </div>
            </div>
          )}

          {/* ElevenLabs Stats Display */}
          {elevenLabsStats && !loadingElevenLabs && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Subscription Tier</span>
                      <Activity className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900 capitalize">
                      {elevenLabsStats.subscription_info.tier}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Status: {elevenLabsStats.subscription_info.status}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Characters Used</span>
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(elevenLabsStats.subscription_info.character_count)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      of {formatNumber(elevenLabsStats.subscription_info.character_limit)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Remaining</span>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(elevenLabsStats.subscription_info.characters_remaining)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">characters left</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Usage</span>
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {elevenLabsStats.subscription_info.usage_percentage.toFixed(1)}%
                    </div>
                    <p className="text-xs text-slate-500 mt-1">of quota used</p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage Progress Bar */}
              <Card className="border-slate-200 shadow-sm mb-6">
                <CardHeader>
                  <CardTitle>Character Usage Overview</CardTitle>
                  <CardDescription>
                    Your current character usage for text-to-speech conversion
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">
                        {formatNumber(elevenLabsStats.subscription_info.character_count)} / {formatNumber(elevenLabsStats.subscription_info.character_limit)} characters
                      </span>
                      <span className={`font-bold ${
                        elevenLabsStats.subscription_info.usage_percentage >= 90 ? 'text-red-600' :
                        elevenLabsStats.subscription_info.usage_percentage >= 70 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {elevenLabsStats.subscription_info.usage_percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full transition-all ${
                          elevenLabsStats.subscription_info.usage_percentage >= 90 ? 'bg-red-500' :
                          elevenLabsStats.subscription_info.usage_percentage >= 70 ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(elevenLabsStats.subscription_info.usage_percentage, 100)}%` }}
                      ></div>
                    </div>
                    {elevenLabsStats.subscription_info.next_character_count_reset_unix > 0 && (
                      <p className="text-xs text-slate-500 mt-2">
                        Resets: {new Date(elevenLabsStats.subscription_info.next_character_count_reset_unix * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Subscription Details</CardTitle>
                  <CardDescription>
                    Additional information about your ElevenLabs plan limits and capabilities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TooltipProvider>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-medium">Custom Voice Limit</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-slate-900 text-white">
                              <p>The maximum number of custom voice clones you can create and store in your account.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="font-semibold text-slate-900">
                          {elevenLabsStats.voice_limit || 'Unlimited'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-medium">Can Purchase Extra Characters</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-slate-900 text-white">
                              <p>Indicates whether your subscription allows purchasing additional character quota.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className={`font-semibold ${elevenLabsStats.can_extend_character_limit ? 'text-green-600' : 'text-slate-500'}`}>
                          {elevenLabsStats.can_extend_character_limit ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
